import {
  Box,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDebounce from 'hooks/util/debounce'
import { TEST_IDS } from 'test-ids'

import { type TrxStreamFilters, type TrxStreamType } from './types'

export type TrxStreamFilterBarProps = {
  filters: TrxStreamFilters
  onChange: (patch: Partial<TrxStreamFilters>) => void
}

const TYPE_OPTIONS: TrxStreamType[] = ['all', 'trx', 'log', 'can', 'user_can']

export default function TrxStreamFilterBar(
  props: TrxStreamFilterBarProps,
): ReactElement {
  const { t } = useTranslation()
  const { filters, onChange } = props
  const [serialNumberInput, setSerialNumberInput] = useState(
    filters.serialNumber,
  )
  const debouncedSerialNumberChange = useDebounce(
    (value: string) => onChange({ serialNumber: value }),
    400,
  )

  useEffect(() => {
    // A pending debounced call (e.g. from typing) must not be allowed to
    // fire after the filter value has been reset/changed externally
    // (Clear Filters, or any other update to filters.serialNumber) -
    // otherwise it would reapply the stale value once its 400ms elapses.
    setSerialNumberInput(filters.serialNumber)
    debouncedSerialNumberChange.cancel?.()
  }, [filters.serialNumber, debouncedSerialNumberChange])

  useEffect(() => {
    return () => {
      debouncedSerialNumberChange.cancel?.()
    }
  }, [debouncedSerialNumberChange])

  const isTrxStartFilterEnabled = filters.type === 'trx'

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-end',
        my: 1,
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="caption">{t('type')}</Typography>
        <Select
          size="small"
          value={filters.type}
          data-testid={TEST_IDS.trxStreamTypeSelect}
          onChange={(event: SelectChangeEvent) =>
            onChange({ type: event.target.value as TrxStreamType })
          }
        >
          {TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption">{t('serial_number')}</Typography>
        <TextField
          size="small"
          value={serialNumberInput}
          data-testid={TEST_IDS.trxStreamSerialNumberInput}
          onChange={(e) => {
            setSerialNumberInput(e.target.value)
            debouncedSerialNumberChange(e.target.value)
          }}
        />
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption">{t('instance_id')}</Typography>
        <TextField
          size="small"
          value={filters.instanceId}
          data-testid={TEST_IDS.trxStreamInstanceIdInput}
          onChange={(e) => onChange({ instanceId: e.target.value })}
        />
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption">{t('record_number')}</Typography>
        <TextField
          size="small"
          value={filters.recordNumber}
          data-testid={TEST_IDS.trxStreamRecordNumberInput}
          onChange={(e) => onChange({ recordNumber: e.target.value })}
        />
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption">{t('upload_date_from')}</Typography>
        <TextField
          type="date"
          size="small"
          value={filters.uploadFrom || ''}
          data-testid={TEST_IDS.trxStreamUploadFromInput}
          onChange={(e) =>
            onChange({ uploadFrom: e.target.value || undefined })
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="caption">{t('upload_date_to')}</Typography>
        <TextField
          type="date"
          size="small"
          value={filters.uploadTo || ''}
          data-testid={TEST_IDS.trxStreamUploadToInput}
          onChange={(e) => onChange({ uploadTo: e.target.value || undefined })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      <Tooltip
        title={
          isTrxStartFilterEnabled ? '' : t('trx_start_filter_disabled_hint')
        }
      >
        <Stack spacing={0.5}>
          <Typography variant="caption">{t('start_time_from')}</Typography>
          <TextField
            type="date"
            size="small"
            disabled={!isTrxStartFilterEnabled}
            value={filters.startFrom || ''}
            data-testid={TEST_IDS.trxStreamStartFromInput}
            onChange={(e) =>
              onChange({ startFrom: e.target.value || undefined })
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Tooltip>
      <Tooltip
        title={
          isTrxStartFilterEnabled ? '' : t('trx_start_filter_disabled_hint')
        }
      >
        <Stack spacing={0.5}>
          <Typography variant="caption">{t('start_time_to')}</Typography>
          <TextField
            type="date"
            size="small"
            disabled={!isTrxStartFilterEnabled}
            value={filters.startTo || ''}
            data-testid={TEST_IDS.trxStreamStartToInput}
            onChange={(e) => onChange({ startTo: e.target.value || undefined })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Tooltip>
    </Box>
  )
}
